import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { assetUrl, formatDate, toExcerpt } from '../Common/useEntries';
import { eventCategory } from './eventFilters';
import EventCard from './EventCard';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
    feature: {
        display: 'grid',
        gridTemplateColumns: '1.05fr 1fr',
        minHeight: 400,
        backgroundColor: tokens.white,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
            minHeight: 0,
        },
    },
    media: {
        backgroundColor: tokens.blue50,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: 400,
        [theme.breakpoints.down('sm')]: { minHeight: 220 },
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: theme.spacing(6),
        [theme.breakpoints.down('sm')]: { padding: theme.spacing(3.5) },
    },
    meta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.muted,
    },
    badge: {
        backgroundColor: tokens.blue50,
        color: tokens.blue700,
        padding: '4px 10px',
        borderRadius: 4,
    },
    title: {
        marginBottom: theme.spacing(2),
        color: tokens.ink,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    excerpt: {
        color: tokens.inkSoft,
        fontSize: '1rem',
        lineHeight: 1.7,
        margin: `0 0 ${theme.spacing(3.5)}px`,
        display: '-webkit-box',
        WebkitLineClamp: 4,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    cta: { alignSelf: 'flex-start' },
}));

/**
 * A news entry rendered either as the homepage featured panel
 * (`variant="slider"`) or as a standard card in a listing grid.
 */
export default function Event({ events, variant }) {
    const classes = useStyles();

    if (!events?.fields) return null;
    if (variant !== 'slider') return <EventCard event={events} />;

    const { title, slug, body, description, eventimage, date } = events.fields;
    const image = assetUrl(eventimage);
    const published = formatDate(date || events.sys?.createdAt);
    const category = eventCategory(events);

    return (
        <article className={classes.feature}>
            <div
                className={classes.media}
                style={image ? { backgroundImage: `url(${image})` } : undefined}
                role="img"
                aria-label={title || 'Featured news'}
            />
            <div className={classes.body}>
                <div className={classes.meta}>
                    <span className={classes.badge}>{category || 'Featured'}</span>
                    {published && <span>{published}</span>}
                </div>

                <Typography variant="h3" component="h2" className={classes.title}>
                    {title}
                </Typography>

                <p className={classes.excerpt}>{toExcerpt(description || body, 260)}</p>

                <Button
                    className={classes.cta}
                    component={RouterLink}
                    to={`/eventdetails/${slug}`}
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                >
                    Read the full story
                </Button>
            </div>
        </article>
    );
}
